import WorkflowStages from 'common/Workflows/WorkflowStages';

const allowedFormNames = [
  'w8ben-form',
  'w8bene-form',
  'crscp-form',
  'crse-form',
  'crsi-form',
  'w8eci-form',
  'w8exp-form',
  'fatca_crse-form',
  'fatca_crsi-form',
  'w8imy-form',
  'rc518-form',
  'rc519-form',
  'w9-form'
];

const trackedStages = [
  WorkflowStages.UnderInitialReview,
  WorkflowStages.UnderSecondaryReview,
  WorkflowStages.UnderExpertReview
];

const digitalEntryTrackedStages = [
  WorkflowStages.UnderSecondaryReview,
  WorkflowStages.UnderExpertReview
];

// Some input names are structured as 'part3.entityFlags[0][legalPersonEntityFlags][controlByOwnership]', which we turned into part3.entityFlags[0].legalPersonEntityFlags.controlByOwnership,
// removing brackets which contain alphabetical characters, keeping those with just numerical characters for array indexing purposes
export function NormalizeInputName(inputName) {
  return inputName.replace(/\[(\D[^\]]*)\]/g, '.$1');
}

export function GetFieldChanges(inputName, formChanges, iValue = null) {
  const fieldPathParts = NormalizeInputName(inputName).split('.');
  let data = formChanges;

  if (data && typeof data === 'object' && Object.keys(data).length > 0) {
    for (let i = 0; i < fieldPathParts.length; i += 1) {
      if (!data) {
        return [];
      }

      const isLastPart = i === fieldPathParts.length - 1;
      let partName = fieldPathParts[i];
      let arrayIndex = null;

      if (partName.includes('[') && partName.includes(']')) {
        const firstBracketIndex = partName.indexOf('[');
        const lastBracketIndex = partName.indexOf(']');

        arrayIndex = partName.substring(firstBracketIndex + 1, lastBracketIndex);
        partName = partName.substring(0, firstBracketIndex);
      }

      const lowerCaseKey = Object.keys(data).find(x => x.toLowerCase() === partName.toLowerCase());

      if (data[lowerCaseKey] === undefined) {
        return [];
      }

      if (isLastPart && iValue !== null) {
        data = data[lowerCaseKey];

        // We don't return the items directly here for RadioInput / iValue fields as all array indexes need to be searched
        return data ?? [];
      }

      if (arrayIndex !== null) {
        if (isLastPart) {
          if (arrayIndex > data.length - 1) {
            return [];
          }

          if (arrayIndex !== null) {
            data = data[lowerCaseKey];
            return data[arrayIndex]?.items ?? [];
          }

          return [];
        }

        data = data[lowerCaseKey];
        data = data[arrayIndex ?? 0];
      } else {
        if (isLastPart) {
          // Edge case scenario where single value is an array value with no array index given in input name (e.g FATCA CRSE reasonGiinNotProvided)
          if (Array.isArray(data[lowerCaseKey])) {
            data = data[lowerCaseKey];
            return data[0]?.items ?? [];
          }

          return data[lowerCaseKey]?.items ?? [];
        }

        data = data[lowerCaseKey];
      }
    }
  }

  return [];
}

export function GetInitialValue(inputName, initialFormValues, iValue = null) {
  const fieldPathParts = NormalizeInputName(inputName).split('.');
  let data = initialFormValues;

  if (data && typeof data === 'object' && Object.keys(data).length > 0) {
    for (let i = 0; i < fieldPathParts.length; i += 1) {
      if (!data) {
        return '';
      }

      const isLastPart = i === fieldPathParts.length - 1;
      let partName = fieldPathParts[i];
      let arrayIndex = null;

      if (partName.includes('[') && partName.includes(']')) {
        const firstBracketIndex = partName.indexOf('[');
        const lastBracketIndex = partName.indexOf(']');

        arrayIndex = partName.substring(firstBracketIndex + 1, lastBracketIndex);
        partName = partName.substring(0, firstBracketIndex);
      }

      const lowerCaseKey = Object.keys(data).find(x => x.toLowerCase() === partName.toLowerCase());

      if (data[lowerCaseKey] === undefined) {
        return '';
      }

      if (isLastPart && iValue !== null) {
        data = data[lowerCaseKey];

        // We don't return the items directly here for RadioInput / iValue fields as all array indexes need to be searched
        return data ?? [];
      }

      if (arrayIndex !== null) {
        data = data[lowerCaseKey];

        if (data) {
          if (isLastPart) {
            if (arrayIndex > data.length - 1) {
              return '';
            }

            return data[arrayIndex] ?? '';
          }

          data = data[arrayIndex ?? 0];
        }
      } else {
        if (isLastPart) {
          return data[lowerCaseKey] ?? '';
        }

        data = data[lowerCaseKey];
      }
    }
  }

  return '';
}

export function CheckFieldHasPreviousReviewChanges(formChanges, inputName, initialFormValues, iValue = null) {
  if (formChanges) {
    const fieldChanges = GetFieldChanges(inputName, formChanges, iValue);
    const isDigitalEntryForm = !initialFormValues?.isOnlineForm && !initialFormValues?.hasSavedPdf;

    // RadioInput / iValue that are array fields must be handled differently as changes can include multiple values,
    // and all array indexes of the field's changes need to be searched
    if (iValue && Array.isArray(fieldChanges)) {
      const hasChangesInReview = fieldChanges.some((change) => {
        const hasPreviousChanges = change && change.items?.some(fieldChange => (isDigitalEntryForm ? digitalEntryTrackedStages.includes(fieldChange.updateStage) : trackedStages.includes(fieldChange.updateStage))
        && (fieldChange.oldValue?.split(',')?.some(x => x === iValue) || fieldChange.newValue?.split(',')?.some(x => x === iValue)));

        return hasPreviousChanges;
      });

      return hasChangesInReview;
    }

    // Some RadioInput / iValue fields are not arrays but return the parent object like above, so the items property must be accessed as below
    if (iValue && !Array.isArray(fieldChanges)) {
      const hasChangesInReview = fieldChanges?.items?.some(fieldChange => (isDigitalEntryForm ? digitalEntryTrackedStages.includes(fieldChange.updateStage) : trackedStages.includes(fieldChange.updateStage))
        && (fieldChange.oldValue === iValue || fieldChange.newValue === iValue));

      return hasChangesInReview;
    }

    // Non iValue fields
    if (fieldChanges.length > 0) {
      const hasChangesInReview = fieldChanges.some(fieldChange => (isDigitalEntryForm ? digitalEntryTrackedStages.includes(fieldChange.updateStage) : trackedStages.includes(fieldChange.updateStage)));

      return hasChangesInReview;
    }
  }

  return false;
}

export function CheckHasReviewChanges(valueChanges, iValue, initialFormValues) {
  if (valueChanges) {
    const isDigitalEntryForm = !initialFormValues?.isOnlineForm && !initialFormValues?.hasSavedPdf;

    // RadioInput / iValue that are array fields must be handled differently as changes can include multiple values,
    // and all array indexes of the field's changes need to be searched
    if (iValue && Array.isArray(valueChanges)) {
      const hasChangesInReview = valueChanges.some((change) => {
        const hasPreviousChanges = change && change.items?.some(fieldChange => (isDigitalEntryForm ? digitalEntryTrackedStages.includes(fieldChange.updateStage) : trackedStages.includes(fieldChange.updateStage))
        && (fieldChange.oldValue?.split(',')?.some(x => x === iValue) || fieldChange.newValue?.split(',')?.some(x => x === iValue)));

        return hasPreviousChanges;
      });

      return hasChangesInReview;
    }

    // Some RadioInput / iValue fields are not arrays but return the parent object like above, so the items property must be accessed as below
    if (iValue && !Array.isArray(valueChanges)) {
      const hasChangesInReview = valueChanges?.items?.some(fieldChange => (isDigitalEntryForm ? digitalEntryTrackedStages.includes(fieldChange.updateStage) : trackedStages.includes(fieldChange.updateStage))
        && (fieldChange.oldValue?.toLowerCase() === iValue.toLowerCase() || fieldChange.newValue?.toLowerCase() === iValue.toLowerCase()));

      return hasChangesInReview;
    }

    // Non iValue fields
    if (!Array.isArray(valueChanges) && valueChanges?.items?.length > 0) {
      const hasChangesInReview = valueChanges.items?.some(fieldChange => (isDigitalEntryForm ? digitalEntryTrackedStages.includes(fieldChange.updateStage) : trackedStages.includes(fieldChange.updateStage)));

      return hasChangesInReview;
    }
    if (Array.isArray(valueChanges) && valueChanges[0]?.items?.length > 0) {
      const hasChangesInReview = valueChanges[0].items?.some(fieldChange => (isDigitalEntryForm ? digitalEntryTrackedStages.includes(fieldChange.updateStage) : trackedStages.includes(fieldChange.updateStage)));

      return hasChangesInReview;
    }
  }

  return false;
}

export function CheckFieldHasCurrentReviewChanges(input, initialValue, initialFormValues, iValue = null, previousValue = null, trimValues = false) {
  const isDigitalEntryForm = !initialFormValues?.isOnlineForm && !initialFormValues?.hasSavedPdf;
  let hasCurrentReviewChanges = false;

  if (isDigitalEntryForm && initialFormValues?.currentWorkflowStage === WorkflowStages.UnderInitialReview) {
    return {
      initialFieldValue: null,
      hasCurrentReviewChanges,
      ignoreChanges: true
    };
  }

  const initialFieldValue = initialValue ?? GetInitialValue(input.name, initialFormValues, iValue);

  if (iValue == null) {
    if (trimValues ? initialFieldValue.trim() !== input.value.trim() : initialFieldValue !== input.value) {
      hasCurrentReviewChanges = true;
    }
  } else if ((!isDigitalEntryForm || (isDigitalEntryForm && initialFormValues?.currentWorkflowStage !== WorkflowStages.UnderInitialReview))) {
    let parsedIValue = iValue;
    if (iValue.toLowerCase() === 'true' || iValue.toLowerCase() === 'false') {
      // convert string to bool where needed
      parsedIValue = iValue.toLowerCase() === 'true';
    }
    if (!Array.isArray(input.value) && !Array.isArray(previousValue)) {
      // clearing originally filled field
      if (initialValue === parsedIValue && input.value !== parsedIValue) {
        hasCurrentReviewChanges = true;
      // filling originally filled field
      } else if (input.value === parsedIValue && initialValue === parsedIValue) {
        hasCurrentReviewChanges = false;
      // filling originally empty field
      } else if (input.value === parsedIValue) {
        hasCurrentReviewChanges = true;
      // clearing originally empty field
      } else {
        hasCurrentReviewChanges = false;
      }
    } else {
      const fieldHasValues = Array.isArray(input.value);
      const fieldHasOldValues = Array.isArray(previousValue);

      if ((fieldHasValues && input.value.some(x => x === iValue)) || (fieldHasOldValues && previousValue.some(x => x === iValue))) {
        // comparing initial and form values on component load
        if (fieldHasValues && Array.isArray(input.value) && Array.isArray(initialValue) && input.value === initialValue) {
          hasCurrentReviewChanges = false;
        // reverting filled field back to original filled value
        } else if (fieldHasValues && input.value.some(x => x === initialValue)) {
          hasCurrentReviewChanges = false;
        // filling empty field
        } else if (fieldHasValues && input.value.some(x => x === iValue)) {
          hasCurrentReviewChanges = true;
        // clearing originally empty field by selecting a different field
        } else if (fieldHasValues && input.value.some(x => x !== iValue) && initialValue === '') {
          hasCurrentReviewChanges = false;
        // clearing originally filled field by selecting a different field
        } else if (initialValue === iValue && fieldHasValues && !input.value.some(x => x === iValue)) {
          hasCurrentReviewChanges = true;
        // clearing originally filled field by deselecting it
        } else if (initialValue === iValue && !fieldHasValues && !input.value !== iValue) {
          hasCurrentReviewChanges = true;
        // clearing originally empty field
        } else {
          hasCurrentReviewChanges = false;
        }
      }
    }
  }

  return {
    initialFieldValue,
    hasCurrentReviewChanges
  };
}

export function GetFormStateName(state) {
  if (state) {
    return Object.getOwnPropertyNames(state).find(name => allowedFormNames.includes(name));
  }

  return null;
}
